/* CatalogSetupWizard — 4-step setup flow for sellers configuring their
 * Catalog app:
 *   1. Industry      — Beauty (only one) + subcategories
 *   2. Template      — Catalog (image grid) or Linesheet (table)
 *   3. Fields        — Toggle field visibility, live preview
 *   4. Finish        — Recap + Continue-to-Import CTA
 *
 * The import work itself happens on /seller/catalog/import — kept
 * separate so the wizard handles CONFIGURATION and the import page
 * handles EXECUTION. One source of truth for each.
 *
 * Routes:
 *   /seller/catalog/setup            → always starts at Step 1
 *   /seller/catalog/setup/:step      → deep link to a single step
 */
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "../../lib/api";
import { toast } from "sonner";
import { ArrowLeft, X, ChevronRight, RotateCcw } from "lucide-react";
import StepIndustry from "../../components/catalog-setup/StepIndustry";
import StepTemplate from "../../components/catalog-setup/StepTemplate";
import StepFields from "../../components/catalog-setup/StepFields";
import StepFinish from "../../components/catalog-setup/StepFinish";

// Wizard structure — 4 steps. Custom data points live INSIDE the
// Fields step (saved together) to keep the flow short.
const STEP_ORDER = ["industry", "template", "fields", "finish"];
const STEP_LABELS = {
  industry: "Industry",
  template: "Template",
  fields:   "Fields",
  finish:   "Review",
};

export default function CatalogSetupWizard() {
  const nav = useNavigate();
  const { step: stepFromUrl } = useParams();
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState("industry");
  const [industryPreset, setIndustryPreset] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/seller/catalog-setup/state");
      setState(r.data || {});
      // URL deep-link wins, else always start at Step 1 (Industry).
      // Resume behaviour is opt-in via the URL — so clicking "Setup
      // wizard" lands you on step 1, not wherever you left off.
      const next = stepFromUrl && STEP_ORDER.includes(stepFromUrl) ? stepFromUrl : "industry";
      setStep(next);
    } catch (e) {
      if (e.response?.data?.detail?.code === "catalog_app_not_available") {
        toast.error("The Catalog app is not available on your account.");
        nav("/seller/catalog");
        return;
      }
      toast.error("Could not load setup state");
    } finally { setLoading(false); }
    // eslint-disable-next-line
  }, [stepFromUrl]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!state?.industry_id) { setIndustryPreset(null); return; }
    api.get(`/seller/catalog-setup/industries/${state.industry_id}`)
       .then(r => setIndustryPreset(r.data))
       .catch(() => setIndustryPreset(null));
  }, [state?.industry_id]);

  const persist = async (patch) => {
    try {
      const r = await api.put("/seller/catalog-setup/state", patch);
      setState(r.data || state);
    } catch (_e) { /* silent */ }
  };

  const goto = (next) => {
    setStep(next);
    persist({ current_step: next });
  };

  const onBack = () => {
    const idx = STEP_ORDER.indexOf(step);
    if (idx > 0) goto(STEP_ORDER[idx - 1]);
  };
  const onNext = () => {
    const idx = STEP_ORDER.indexOf(step);
    if (idx < STEP_ORDER.length - 1) goto(STEP_ORDER[idx + 1]);
  };

  const skipWizard = async () => {
    if (!window.confirm("Skip setup and go straight to the catalog? You can re-run setup any time from the Setup wizard button.")) return;
    try {
      await api.post("/seller/catalog-setup/skip");
      toast.success("Setup skipped");
      nav("/seller/catalog");
    } catch (_e) { toast.error("Could not skip"); }
  };

  const resetWizard = async () => {
    if (!window.confirm("Reset wizard? Your catalog data stays intact — only the wizard progress is cleared.")) return;
    try {
      await api.post("/seller/catalog-setup/reset");
      setStep("industry");
      setState({});
      toast.success("Wizard reset");
    } catch (_e) { toast.error("Could not reset"); }
  };

  if (loading) return <div className="grid place-items-center h-screen text-foreground/45 text-sm">Loading…</div>;

  return (
    <div className="min-h-screen bg-background" data-testid="catalog-setup-wizard">
      {/* Header */}
      <div className="border-b border-border bg-white sticky top-0 z-10 px-8 py-4 flex items-center justify-between">
        <button onClick={() => nav("/seller/catalog")} className="text-foreground/65 hover:text-foreground inline-flex items-center gap-1.5 text-sm">
          <ArrowLeft className="w-4 h-4"/>Back to catalog
        </button>
        <div className="text-[10px] mono uppercase tracking-[0.24em] text-foreground/55">
          Catalog setup wizard
        </div>
        <div className="flex items-center gap-2">
          <button onClick={resetWizard} data-testid="wizard-reset"
            className="text-[12px] text-foreground/55 hover:text-foreground inline-flex items-center gap-1">
            <RotateCcw className="w-3 h-3"/>Reset
          </button>
          <button onClick={skipWizard} data-testid="wizard-skip"
            className="text-[12px] text-foreground/55 hover:text-foreground inline-flex items-center gap-1">
            <X className="w-3 h-3"/>Skip setup
          </button>
        </div>
      </div>

      {/* Stepper */}
      <div className="border-b border-border bg-white px-8 py-3">
        <div className="max-w-5xl mx-auto flex items-center gap-2 text-[11px]">
          {STEP_ORDER.map((s, i) => {
            const idx = STEP_ORDER.indexOf(step);
            const done = i < idx;
            const active = i === idx;
            return (
              <div key={s} className="flex items-center gap-2">
                <button onClick={() => goto(s)} data-testid={`stepper-${s}`}
                  className={`inline-flex items-center gap-1.5 px-2.5 h-7 rounded-full transition-colors ${active ? "bg-foreground text-background" : done ? "bg-emerald-50 border border-emerald-200 text-emerald-700" : "bg-secondary text-foreground/55"}`}>
                  <span className="mono text-[10px]">{i + 1}</span>
                  <span className="font-medium">{STEP_LABELS[s]}</span>
                </button>
                {i < STEP_ORDER.length - 1 && <ChevronRight className="w-3 h-3 text-foreground/30"/>}
              </div>
            );
          })}
        </div>
      </div>

      {/* Body */}
      <div className="page-shell-narrow py-12">
        {step === "industry" && (
          <StepIndustry
            value={{ industry_id: state?.industry_id, subcategories: state?.subcategories || [] }}
            onChange={(v) => { setState(s => ({ ...s, ...v })); persist(v); }}
            onNext={onNext}
            onBack={() => nav("/seller/catalog")}
          />
        )}
        {step === "template" && (
          <StepTemplate
            value={state?.template_id}
            industryDefault={industryPreset?.template_id}
            onChange={(v) => { setState(s => ({ ...s, template_id: v })); persist({ template_id: v }); }}
            onNext={onNext}
            onBack={onBack}
          />
        )}
        {step === "fields" && (
          <StepFields
            onSaved={() => persist({ fields_step_completed: true, custom_fields_step_completed: true })}
            onNext={onNext}
            onBack={onBack}
          />
        )}
        {step === "finish" && (
          <StepFinish
            state={state}
            industryPreset={industryPreset}
            onBack={onBack}
          />
        )}
      </div>
    </div>
  );
}
